"use client";

import { useState } from "react";
import { useRouter } from "@/lib/spa/navigation";
import Link from "@/lib/spa/Link";
import { CAPABILITIES, CapabilityIcon } from "@/components/marketing/capabilities";
import { Logo } from "@/components/ui/Logo";

// ── Main login page ─────────────────────────────────────────────────────────
export default function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [code, setCode] = useState("");
  const [challenge, setChallenge] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(challenge ? { challenge, code } : { email, password }),
      });
      const data = (await res.json().catch(() => null)) as { error?: string; totpRequired?: boolean; challenge?: string } | null;
      if (data?.totpRequired && data.challenge) {
        setChallenge(data.challenge);
        setCode("");
        return;
      }
      if (res.ok) {
        router.push("/timing/daily-brief");
        router.refresh();
      } else {
        const data = await res.json();
        if (challenge) setChallenge("");
        setError(data.error || "Login failed.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="flex min-h-dvh flex-col-reverse bg-canvas text-ink lg:flex-row"
      style={{
        paddingTop: "var(--mpp-sat)",
        paddingBottom: "var(--mpp-sab)",
        paddingLeft: "var(--mpp-sal)",
        paddingRight: "var(--mpp-sar)",
      }}
    >
      {/* ── Left: brand + clickable capabilities ── */}
      <div className="flex flex-1 flex-col justify-center px-5 py-10 sm:px-12 lg:px-16 xl:px-24">
        <div className="mx-auto w-full max-w-xl">
          {/* Logo */}
          <div className="mb-12 flex items-center gap-3">
            <Logo size={40} />
            <div>
              <div className="text-lg font-bold leading-none text-ink">Afiat</div>
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">MarketPulse Pro</div>
            </div>
          </div>

          <h1 className="text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-4xl">
            Market intelligence,
            <br />
            <span className="text-accent">from the top down.</span>
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-ink-muted">
            One workspace for market timing, breadth, volume-price analysis, scanning,
            seasonality and fundamentals. Explore what it does:
          </p>

          {/* Clickable capabilities → /features#slug */}
          <div className="mt-8 space-y-1">
            {CAPABILITIES.map((c) => (
              <Link
                key={c.slug}
                href={`/features#${c.slug}`}
                className="group flex items-center gap-3 rounded-xl border border-transparent px-3 py-2.5 transition-colors hover:border-line hover:bg-surface-muted"
              >
                <CapabilityIcon>{c.icon}</CapabilityIcon>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-ink">{c.title}</div>
                  <div className="truncate text-xs text-ink-muted">{c.tagline}</div>
                </div>
                <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-ink-faint transition-all group-hover:translate-x-0.5 group-hover:text-accent" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              </Link>
            ))}
          </div>

          <Link
            href="/features"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-accent transition-colors hover:text-accent/80"
          >
            See everything it does, with screenshots
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </Link>
        </div>
      </div>

      {/* ── Right: login card ── */}
      <div className="flex items-center justify-center border-t border-line bg-surface-muted px-5 py-10 sm:px-6 sm:py-14 lg:w-[480px] lg:border-l lg:border-t-0">
        <div className="w-full max-w-sm">
          <div className="rounded-2xl border border-line bg-surface p-8 shadow-panel">
            <div className="mb-8">
              <h2 className="text-xl font-bold text-ink">Welcome back</h2>
              <p className="mt-1 text-sm text-ink-muted">Sign in to your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {challenge ? (
                <div>
                  <label htmlFor="login-code" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                    Authenticator code
                  </label>
                  <input
                    id="login-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                    autoFocus
                    placeholder="6-digit code or a recovery code"
                    className="w-full rounded-xl border border-line-strong bg-surface py-3 px-4 text-base text-ink placeholder:text-ink-faint outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20"
                  />
                </div>
              ) : (
              <>
              {/* Email */}
              <div>
                <label htmlFor="login-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Email
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </span>
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-xl border border-line-strong bg-surface py-3 pl-10 pr-4 text-base text-ink placeholder:text-ink-faint outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label htmlFor="login-password" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  Password
                </label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-faint">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                  </span>
                  <input
                    id="login-password"
                    type={showPass ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="w-full rounded-xl border border-line-strong bg-surface py-3 pl-10 pr-12 text-base text-ink placeholder:text-ink-faint outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint transition-colors hover:text-ink-muted"
                    tabIndex={-1}
                    aria-label={showPass ? "Hide password" : "Show password"}
                  >
                    {showPass ? (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              </>
              )}

              {/* Error */}
              {error && (
                <div className="flex items-center gap-2 rounded-lg border border-bear/30 bg-bear-soft px-3 py-2 text-sm text-bear">
                  <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-accent py-3 text-base font-semibold text-on-accent transition-colors hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4" />
                      <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                    Signing in…
                  </span>
                ) : challenge ? (
                  "Verify"
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            <div className="mt-6 border-t border-line pt-6 text-center">
              <p className="text-xs text-ink-faint">
                Afiat MarketPulse Pro &copy; {new Date().getFullYear()} Afiatpour LLC
              </p>
            </div>
          </div>

          {/* Status indicators */}
          <div className="mt-4 flex items-center justify-center gap-4 text-xs text-ink-faint">
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
              Secure connection
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
              Real-time data
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
